import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as moment from 'moment';
// import { StreamState } from '../interfaces/stream-state';

export interface StreamState {
    playing: boolean;
    readableCurrentTime: string;
    readableDuration: string;
    duration: number | undefined;
    currentTime: number | undefined;
    canplay: boolean;
    error: boolean;
    url: string;
    playSpeed: number;
  }

@Injectable({
  providedIn: 'root'
})
export class AudioService {
  private stop$ = new Subject<void>();
  private audioObj = new Audio();
  audioEvents = [
    'ended', 'error', 'play', 'playing', 'pause', 'timeupdate', 'canplay', 'loadedmetadata', 'loadstart'
  ];
  private state: StreamState = {
    playing: false,
    readableCurrentTime: '',
    readableDuration: '',
    duration: undefined,
    currentTime: undefined,
    canplay: false,
    error: false,
    url: '',
    playSpeed: 1,
  };
  private stateChange: BehaviorSubject<StreamState> = new BehaviorSubject(this.state);

  private streamObservable(url: string): any {
    return new Observable(observer => {
      // Play audio
      console.log(new Date(), url, this.state.playSpeed);
      this.audioObj.src = url;
      
      this.audioObj.load();
      this.audioObj.play();
      this.audioObj.playbackRate = this.state.playSpeed;
      const handler = (event: Event) => {
        this.updateStateEvents(event);
        observer.next(event);
      };

      this.addEvents(this.audioObj, this.audioEvents, handler);
      return () => {
        // Stop Playing
        this.audioObj.pause();
        this.audioObj.currentTime = 0;
        // remove event listeners
        this.removeEvents(this.audioObj, this.audioEvents, handler);
        // reset state
        this.resetState();
      };
    });
  }

  private prepareObservable(url: string) {
    return new Observable(observer => {
      // Play audio
      console.log(new Date(), url, this.state.playSpeed);
      this.audioObj.src = url;
      
      this.audioObj.load();
      // this.audioObj.play();
      this.audioObj.playbackRate = this.state.playSpeed;
      const handler = (event: Event) => {
        this.updateStateEvents(event);
        observer.next(event);
      };

      this.addEvents(this.audioObj, this.audioEvents, handler);
      return () => {
        // Stop Playing
        this.audioObj.pause();
        this.audioObj.currentTime = 0;
        // remove event listeners
        this.removeEvents(this.audioObj, this.audioEvents, handler);
        // reset state
        this.resetState();
      };
    });
  }

  private addEvents(obj, events, handler): void {
    events.forEach(event => {
      obj.addEventListener(event, handler);
    });
  }

  private removeEvents(obj, events, handler): void {
    events.forEach(event => {
      obj.removeEventListener(event, handler);
    });
  }

  playStream(url: string): any {
    this.state.url = url;
    return this.streamObservable(url).pipe(takeUntil(this.stop$));
  }

  prepareStream(url: string): any {
    this.state.url = url;
    return this.prepareObservable(url).pipe(takeUntil(this.stop$));
  }

  setPlaySpeed(playSpeed: number) {
    this.state.playSpeed = playSpeed;
    this.audioObj.playbackRate = this.state.playSpeed;
  }

  play(): void {
    this.audioObj.play();
  }

  pause(): void {
    this.audioObj.pause();
  }

  stop(): void {
    this.stop$.next();
  }

  seekTo(seconds): void {
    this.pause();
    this.audioObj.currentTime = seconds;
    this.play();
  }

  formatTime(time: number, format: string = 'mm:ss' /* 'HH:mm:ss' */): string {
    const momentTime = time * 1000;
    return moment.utc(momentTime).format(format);
  }

  private updateStateEvents(event: Event): void {
    switch (event.type) {
      case 'canplay':
        this.state.duration = this.audioObj.duration;
        this.state.readableDuration = this.formatTime(this.state.duration);
        this.state.canplay = true;
        break;
      case 'playing':
        this.state.playing = true;
        break;
      case 'pause':
        this.state.playing = false;
        break;
      case 'timeupdate':
        this.state.currentTime = this.audioObj.currentTime;
        this.state.readableCurrentTime = this.formatTime(this.state.currentTime);
        break;
      case 'error':
        this.resetState();
        this.state.error = true;
        break;
    }
    this.stateChange.next(this.state);
  }

  private resetState(): void {
    this.state = {
      playing: false,
      readableCurrentTime: '',
      readableDuration: '',
      duration: undefined,
      currentTime: undefined,
      canplay: false,
      error: false,
      url: '',
      playSpeed: this.state.playSpeed,
    };
  }

  getState(): Observable<StreamState> {
    return this.stateChange.asObservable();
  }
}
